import { Company } from '@centecomm/portal-service';
import useSubscription from 'Core/Subscription/useSubscription';
import { useLayoutEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useSearchParam } from 'react-use';

const PECO_ORG_CODE_TOKEN = 'peco';

/**
 * usePECOAuth0OrgBypass - bypasses Auth0 login and sets the organization based on the `friendlyId` query param.
 * This is used for PECO to bypass the initial login screen where the users are supposed to input their email
 * for us to determine which Auth0 organization they should be authenticated against.
 * If you want to use this hook, you need to add the friendlyId query param to the URL with the following format:
 * `?friendlyId={internalFriendlyCompanyId}$code=peco`.
 * If, for any reason, you want to clear the user and redirect to the standard login screen, you can add the
 * `clear` query param to the URL, like this: `?clear=true`.
 */
const usePECOAuth0OrgBypass = (userCompanies: Company[] = []): void => {
    const companyFriendlyIdParam = useSearchParam('friendlyId');
    const clearParam = useSearchParam('clear');
    const { clearUser, setOrganization } = useSubscription();
    const { push } = useHistory();

    useLayoutEffect(() => {
        if (clearParam) {
            clearUser();
            push('/');
        }

        if (!companyFriendlyIdParam || !userCompanies) return;

        const [targetFriendlyId, orgName] = companyFriendlyIdParam.split('$code=');

        if (!targetFriendlyId || orgName !== PECO_ORG_CODE_TOKEN) return;

        const orgId = userCompanies.find((company) => company.friendlyId === targetFriendlyId)?.orgId;

        if (!orgId) return;

        setOrganization(orgId);
        push('/');
    }, [companyFriendlyIdParam, clearParam, userCompanies]);
};

export default usePECOAuth0OrgBypass;
